import { useState } from "react";
import API_BASE_URL from "../config";

const initialForm = {
    controlNo: "",
    documentNo: "",
    dateWithdrawn: new Date().toISOString().split("T")[0],
    timeStarted: "",
    timeFinished: "",
    isConversion: false,
    isMarketing: false,
    isRepacking: false,
    isRepair: false,
    requestedBy: "",
    acknowledgedBy: "",
    withdrawnBy: "",
    receivedBy: "",
    items: [
        { itemCode: "", itemDescription: "", uom: "", quantity: "", purposeOfWithdrawal: "" },
    ],
};

const generateControlNo = () => {
    const now = new Date();
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const rand = String(Math.floor(Math.random() * 100000)).padStart(6, "0");
    return `WF-${y}${mo}${d}${rand}`;
};

export default function FG_Withdrawal_FORM() {
    const [form, setForm] = useState({ ...initialForm, controlNo: generateControlNo() });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [savedData, setSavedData] = useState(null);

    const handleField = (field, value) => setForm((f) => ({ ...f, [field]: value }));
    const handleCheck = (field) => setForm((f) => ({ ...f, [field]: !f[field] }));
    const handleItem = (idx, field, value) => {
        const items = [...form.items];
        items[idx] = { ...items[idx], [field]: value };
        setForm((f) => ({ ...f, items }));
    };
    const addItem = () =>
        setForm((f) => ({
            ...f,
            items: [...f.items, { itemCode: "", itemDescription: "", uom: "", quantity: "", purposeOfWithdrawal: "" }],
        }));
    const removeItem = (idx) =>
        setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                controlNo: form.controlNo,
                documentNo: form.documentNo || null,
                dateWithdrawn: form.dateWithdrawn,           // "2026-05-04" — DateOnly accepts this
                timeStarted: form.timeStarted ? form.timeStarted + ":00" : null,  // "01:01:00"
                timeFinished: form.timeFinished ? form.timeFinished + ":00" : null, // "10:04:00"
                isConversion: form.isConversion,
                isMarketing: form.isMarketing,
                isRepacking: form.isRepacking,
                isRepair: form.isRepair,
                requestedBy: form.requestedBy || null,
                acknowledgedBy: form.acknowledgedBy || null,
                withdrawnBy: form.withdrawnBy || null,
                receivedBy: form.receivedBy || null,
                items: form.items.map((item) => ({
                    itemCode: item.itemCode,
                    itemDescription: item.itemDescription,
                    uom: item.uom,
                    quantity: Number(item.quantity) || 0,
                    purposeOfWithdrawal: item.purposeOfWithdrawal || null,
                })),
            };

            const res = await fetch(`${API_BASE_URL}api/FGWithdrawal`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "*/*" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                // Show detailed error from ModelState if available
                const details = err?.errors
                    ? Object.entries(err.errors).map(([k, v]) => `${k}: ${v}`).join(" | ")
                    : null;
                throw new Error(details || err?.message || `Server error ${res.status}`);
            }

            const result = await res.json();
            setSavedData(result);
            setSubmitted(true);
        } catch (err) {
            setError(err.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({ ...initialForm, controlNo: generateControlNo() });
        setSubmitted(false);
        setSavedData(null);
        setError(null);
    };

    // ── SUCCESS ──────────────────────────────────────────────────────────────
    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md w-full">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Saved to Database!</h2>
                    <p className="text-gray-500 mb-1">Control No: <span className="font-semibold text-blue-700">{savedData?.controlNo || form.controlNo}</span></p>
                    <p className="text-gray-400 text-sm mb-1">ID: <span className="font-mono text-gray-600">#{savedData?.id}</span></p>
                    <p className="text-gray-400 text-sm mb-6">Withdrawal form submitted successfully.</p>
                    <button onClick={handleReset}
                        className="bg-blue-700 text-white px-6 py-2.5 rounded-lg hover:bg-blue-800 transition font-semibold">
                        New Form
                    </button>
                </div>
            </div>
        );
    }

    // ── MAIN ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-100 font-sans">
            <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">

                {/* Header */}
                <div className="bg-blue-700 rounded-2xl px-8 py-5 flex items-center justify-between shadow-md">
                    <div>
                        <h1 className="text-white text-2xl font-bold tracking-tight">FG Withdrawal Form</h1>
                        <p className="text-blue-200 text-sm mt-0.5">Finished Goods Withdrawal — Mega Soft Hygienic Products, Inc.</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <span className="bg-white/20 text-white font-bold text-sm px-4 py-1.5 rounded-lg border border-white/30 tracking-wider">
                            {form.controlNo}
                        </span>
                        <span className="text-blue-200 text-xs">
                            {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                        </span>
                    </div>
                </div>

                {/* Error Banner — now shows ModelState field errors too */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-xl text-sm flex items-center gap-3">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                    </div>
                )}

                {/* Section 1: Withdrawal Information */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <h2 className="text-base font-bold text-gray-800 border-l-4 border-blue-700 pl-4 mb-6">Withdrawal Information</h2>
                    <div className="grid grid-cols-3 gap-5 mb-5">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Control No.</label>
                            <input value={form.controlNo} readOnly
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Document No.</label>
                            <input value={form.documentNo} onChange={e => handleField("documentNo", e.target.value)}
                                placeholder="Enter document no."
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                Date Withdrawn <span className="text-red-500">*</span>
                            </label>
                            <input type="date" value={form.dateWithdrawn} onChange={e => handleField("dateWithdrawn", e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Time Started</label>
                            <input type="time" value={form.timeStarted} onChange={e => handleField("timeStarted", e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Time Finished</label>
                            <input type="time" value={form.timeFinished} onChange={e => handleField("timeFinished", e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">Purpose of Withdrawal</label>
                        <div className="flex flex-wrap gap-5">
                            {[
                                { key: "isConversion", label: "Conversion" },
                                { key: "isMarketing", label: "Marketing" },
                                { key: "isRepacking", label: "Repacking" },
                                { key: "isRepair", label: "Repair" },
                            ].map(({ key, label }) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer group">
                                    <div onClick={() => handleCheck(key)}
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${form[key] ? "bg-blue-700 border-blue-700" : "border-gray-300 group-hover:border-blue-400"
                                            }`}>
                                        {form[key] && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="text-sm text-gray-700 font-medium">{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Section 2: Items Requested */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-base font-bold text-gray-800 border-l-4 border-blue-700 pl-4">Items Requested</h2>
                        <button onClick={addItem}
                            className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Item
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    {["#", "Item Code", "Description", "UOM", "Qty", "Purpose of Withdrawal", ""].map((h) => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {form.items.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/50">
                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs w-8">{idx + 1}</td>
                                        <td className="px-4 py-3">
                                            <input value={item.itemCode} onChange={e => handleItem(idx, "itemCode", e.target.value)}
                                                placeholder="4008"
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input value={item.itemDescription} onChange={e => handleItem(idx, "itemDescription", e.target.value)}
                                                placeholder="FC/Sample 45g"
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </td>
                                        <td className="px-4 py-3 w-28">
                                            <input value={item.uom} onChange={e => handleItem(idx, "uom", e.target.value)}
                                                placeholder="SACK"
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </td>
                                        <td className="px-4 py-3 w-24">
                                            <input type="number" value={item.quantity} onChange={e => handleItem(idx, "quantity", e.target.value)}
                                                placeholder="0"
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input value={item.purposeOfWithdrawal} onChange={e => handleItem(idx, "purposeOfWithdrawal", e.target.value)}
                                                placeholder="ANNIV Support & Opening Support..."
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                        </td>
                                        <td className="px-4 py-3 w-10">
                                            {form.items.length > 1 && (
                                                <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500 transition">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-5 py-3 text-sm text-blue-800 font-semibold">
                            Total Items: {form.items.length} &nbsp;|&nbsp; Total Qty:{" "}
                            {form.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0)}
                        </div>
                    </div>
                </div>

                {/* Section 3: Signatories */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <h2 className="text-base font-bold text-gray-800 border-l-4 border-blue-700 pl-4 mb-6">Signatories</h2>
                    <div className="grid grid-cols-2 gap-5">
                        {[
                            { key: "requestedBy", label: "Requested By", required: true },
                            { key: "acknowledgedBy", label: "Acknowledged By", required: false },
                            { key: "withdrawnBy", label: "Withdrawn By", required: false },
                            { key: "receivedBy", label: "Received By", required: false },
                        ].map(({ key, label, required }) => (
                            <div key={key}>
                                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                    {label} {required && <span className="text-red-500">*</span>}
                                </label>
                                <input value={form[key]} onChange={e => handleField(key, e.target.value)}
                                    placeholder="Enter name"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                <p className="text-xs text-gray-400 mt-1">Signature over Printed Name</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pb-8">
                    <button onClick={handleReset}
                        className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
                        Reset
                    </button>
                    <button className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Draft
                    </button>
                    <button onClick={handleSubmit} disabled={loading}
                        className="flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition">
                        {loading ? (
                            <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Send to Approvers
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
}